/**
 * Error code to i18n key mapping
 * Maps backend error codes to frontend translation keys
 */

import { ApiErrorCode, ApiErrorCodes } from '../types/api.types';

/**
 * Mapping between backend error codes and i18n translation keys
 */
export const errorCodeToI18nKey: Record<ApiErrorCode, string> = {
  // User errors
  [ApiErrorCodes.USER_LOGIN_EXISTS]: 'error.user_login_exists',
  [ApiErrorCodes.USER_INVALID_AGENCY]: 'error.user_invalid_agency',
  [ApiErrorCodes.USER_INVALID_ROLE]: 'error.user_invalid_role',
  [ApiErrorCodes.USER_NOT_FOUND]: 'error.user_not_found',
  [ApiErrorCodes.USER_INVALID_EMAIL]: 'error.user_invalid_email',
  [ApiErrorCodes.USER_INVALID_LOGIN_FORMAT]: 'error.user_invalid_login_format',
  [ApiErrorCodes.USER_MISSING_REQUIRED_FIELD]: 'error.user_missing_required_field',
  [ApiErrorCodes.USER_INVALID_CIVILITY]: 'error.user_invalid_civility',
  // Agency errors
  [ApiErrorCodes.AGENCY_NOT_FOUND]: 'error.agency_not_found',
  // Service Delivery errors
  [ApiErrorCodes.SD_INVALID_DATE]: 'error.sd_invalid_date',
  [ApiErrorCodes.SD_INVALID_PATRIMONY]: 'error.sd_invalid_patrimony',
  [ApiErrorCodes.SD_INVALID_TYPE]: 'error.sd_invalid_type',
  [ApiErrorCodes.SD_TYPES_REQUIRED]: 'error.sd_types_required',
  [ApiErrorCodes.SD_INVALID_COLLABORATOR]: 'error.sd_invalid_collaborator',
  [ApiErrorCodes.SD_COLLABORATOR_WRONG_AGENCY]: 'error.sd_collaborator_wrong_agency',
  [ApiErrorCodes.SD_INVALID_TIME]: 'error.sd_invalid_time',
  [ApiErrorCodes.SD_INVALID_WEEK_TEMPLATE]: 'error.sd_invalid_week_template',
  [ApiErrorCodes.SD_CREATE_FAILED]: 'error.sd_create_failed',
  [ApiErrorCodes.SD_NOT_FOUND]: 'error.sd_not_found',
  // Week Template errors
  [ApiErrorCodes.WT_INVALID_DAY_SCHEDULE]: 'error.wt_invalid_day_schedule',
  [ApiErrorCodes.WT_CORRUPT_DATA]: 'error.wt_corrupt_data',
  [ApiErrorCodes.WT_NOT_FOUND]: 'error.wt_not_found',
  [ApiErrorCodes.WT_JOB_CREATE_FAILED]: 'error.wt_job_create_failed',
  [ApiErrorCodes.WT_TYPE_ASSOC_FAILED]: 'error.wt_type_assoc_failed',
  [ApiErrorCodes.WT_USER_ASSOC_FAILED]: 'error.wt_user_assoc_failed',
  // Patrimony errors
  [ApiErrorCodes.PAT_NOT_FOUND]: 'error.pat_not_found',
  [ApiErrorCodes.PAT_CREATE_FAILED]: 'error.pat_create_failed',
  [ApiErrorCodes.PAT_INVALID_TYPES]: 'error.pat_invalid_types',
  // Generic errors
  [ApiErrorCodes.MISSING_PARAMETER]: 'error.missing_parameter',
  [ApiErrorCodes.INVALID_DATA]: 'error.invalid_data',
  [ApiErrorCodes.SERVER_ERROR]: 'error.server_error',
};

/**
 * Field name to i18n label key mapping
 * Used to display field names in user-friendly format
 */
export const fieldToI18nLabel: Record<string, string> = {
  login: 'user.login',
  email: 'user.email',
  firstname: 'user.first_name',
  lastname: 'user.last_name',
  agencyId: 'common.agency',
  roleId: 'common.function',
  civility: 'common.civility',
  active: 'common.account',
  // Service Delivery fields
  date: 'intervention.date',
  refPat: 'intervention.ref_patrimony',
  serviceDeliveryTypeIds: 'intervention.type',
  collabIds: 'intervention.Collaborators',
  initialTimeRange: 'intervention.duration',
  weekTemplateId: 'planning.week_template',
  // Patrimony fields
  patrimony: 'patrimony.patrimony',
};

/**
 * HTTP status code to i18n key mapping
 */
export const httpStatusToI18nKey: Record<number, string> = {
  400: 'error.bad_request',
  401: 'error.unauthorized',
  403: 'error.forbidden',
  404: 'error.not_found',
  408: 'error.timeout',
  409: 'error.conflict',
  422: 'error.validation_error',
  500: 'error.server_error',
  502: 'error.server_error',
  503: 'error.service_unavailable',
  504: 'error.timeout',
};

/**
 * Get the i18n key for an error code
 * @param code The API error code
 * @returns The i18n translation key
 */
export function getErrorI18nKey(code?: ApiErrorCode): string | undefined {
  if (!code) return undefined;
  return errorCodeToI18nKey[code];
}

/**
 * Get the i18n key for an HTTP status code
 * @param status The HTTP status code
 * @returns The i18n translation key
 */
export function getHttpStatusI18nKey(status?: number): string | undefined {
  if (!status) return undefined;
  return httpStatusToI18nKey[status];
}

/**
 * Get the i18n label key for a field
 * @param field The field name
 * @returns The i18n label key
 */
export function getFieldI18nLabel(field?: string): string | undefined {
  if (!field) return undefined;
  return fieldToI18nLabel[field];
}

